// src/context/OfflineContext.js
//
// The app-wide "are we actually online right now, and how bad is it" signal
// behind offline mode (this feature's own plan doc, "Real Offline Mode for
// the Cashier/Checkout Screen"). Mirrors SystemConfigContext's own plain
// useState shape (no Redux anywhere in this codebase) — a child of
// <SystemConfigProvider> in App.js, since it reads the "pos" category's
// offline_max_hours setting straight out of that context rather than
// fetching it separately (GetPublicConfig already returns it — see
// ConfigPage.jsx's "pos" schema entry's own doc comment on why that setting
// is public:true).
import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { ping } from "../services/api";
import { useSystemConfig } from "./SystemConfigContext";
import {
  getQueueLength,
  getOfflineSince,
} from "../services/offlineDb";
import { drainQueue } from "../services/offlineSync";

const OfflineContext = createContext();

// How often the ping probe runs while the tab is open — real connectivity,
// not just navigator.onLine (which false-positives/negatives on flaky wifi:
// a captive portal or a dead upstream link can leave the browser reporting
// "online" while every real request fails, and vice versa on some mobile
// networks).
const PING_INTERVAL_MS = 15000;

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(true);
  const [queueLength, setQueueLength] = useState(0);
  const [offlineSince, setOfflineSince] = useState(null);
  const [syncStatus, setSyncStatus] = useState("idle"); // "idle" | "syncing" | "complete" | "failed"
  const { systemSettings } = useSystemConfig();
  const syncingRef = useRef(false);

  const offlineMaxHours = Number(systemSettings?.pos?.offline_max_hours) > 0
    ? Number(systemSettings?.pos?.offline_max_hours)
    : 24;

  const refreshQueueState = useCallback(async () => {
    const [len, since] = await Promise.all([getQueueLength(), getOfflineSince()]);
    setQueueLength(len);
    setOfflineSince(since);
  }, []);

  const syncNow = useCallback(async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    setSyncStatus("syncing");
    try {
      const result = await drainQueue();
      await refreshQueueState();
      setSyncStatus(result?.hasFailures ? "failed" : "complete");
    } catch (err) {
      // drainQueue itself only throws on something unexpected — a genuine
      // per-item failure is caught and recorded inside it (see
      // offlineSync.js) — but never let that surface as an unhandled
      // rejection here.
      console.error("[OfflineContext] sync failed unexpectedly:", err);
      setSyncStatus("failed");
    } finally {
      syncingRef.current = false;
    }
  }, [refreshQueueState]);

  // Real connectivity probe — on an interval, and immediately whenever the
  // browser fires its own online/offline events (a useful hint to re-check
  // sooner, never trusted on its own — see PING_INTERVAL_MS's doc comment).
  useEffect(() => {
    let cancelled = false;

    const checkOnline = async () => {
      const token = localStorage.getItem("token");
      if (!token) return; // not signed in yet (Login screen) — nothing to probe
      try {
        await ping(token);
        if (cancelled) return;
        setIsOnline((prevOnline) => {
          if (!prevOnline) {
            // Just came back — kick off a sync in the background rather
            // than waiting for the next scheduled tick.
            syncNow();
          }
          return true;
        });
      } catch (err) {
        if (cancelled) return;
        setIsOnline(false);
      }
    };

    checkOnline();
    const interval = setInterval(checkOnline, PING_INTERVAL_MS);
    window.addEventListener("online", checkOnline);
    window.addEventListener("offline", checkOnline);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("online", checkOnline);
      window.removeEventListener("offline", checkOnline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Queue length/offline-since are polled rather than pushed — every
  // enqueue happens inside Checkout.js's own call sites, which don't share
  // a direct reference to this context's setters, so a short poll is
  // simpler than threading an event emitter through offlineDb.js for what
  // is, at most, a few-second-stale banner count.
  useEffect(() => {
    refreshQueueState();
    const interval = setInterval(refreshQueueState, 5000);
    return () => clearInterval(interval);
  }, [refreshQueueState]);

  const hoursOffline = offlineSince
    ? (Date.now() - new Date(offlineSince).getTime()) / 3600000
    : 0;
  const offlineCapExceeded = Boolean(offlineSince) && hoursOffline > offlineMaxHours;

  return (
    <OfflineContext.Provider
      value={{
        isOnline,
        queueLength,
        offlineSince,
        offlineMaxHours,
        hoursOffline,
        offlineCapExceeded,
        syncStatus,
        syncNow,
        refreshQueueState,
      }}
    >
      {children}
    </OfflineContext.Provider>
  );
};

export const useOffline = () => useContext(OfflineContext);
